import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, FileUp, PackageOpen, RotateCcw, Search, Send, Trash2, Truck, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { customerGiftsApi, type CustomerGift, type CustomerGiftListParams } from '@/api/customer-gifts'
import { giftProductsApi } from '@/api/gift-products'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BatchDeleteDialog } from './components/BatchDeleteDialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { showError } from '@/lib/error'

type FilterValues = CustomerGiftListParams & { date_range?: [string, string] }

export function CustomerGiftsPage() {
	const queryClient = useQueryClient()
	const importInputRef = useRef<HTMLInputElement>(null)
	const shippingInputRef = useRef<HTMLInputElement>(null)
	const [filters, setFilters] = useState<FilterValues>({ scope: 'my' })
	const [searchFilters, setSearchFilters] = useState<FilterValues>({ scope: 'my' })
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
	const [selected, setSelected] = useState<number[]>([])
	const [batchConfirmOpen, setBatchConfirmOpen] = useState(false)

	const { data: productOptions } = useQuery({
		queryKey: ['gift-products', 'options'],
		queryFn: () => giftProductsApi.options(),
	})

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['customer-gifts', searchFilters, page, pageSize],
		queryFn: () => customerGiftsApi.list({
			...searchFilters,
			page,
			page_size: pageSize,
			sort: 'id',
			order: 'descending',
		}),
	})

	const invalidateList = () => {
		queryClient.invalidateQueries({ queryKey: ['customer-gifts'] })
		queryClient.invalidateQueries({ queryKey: ['gift-products'] })
		setSelected([])
	}

	const importMutation = useMutation({
		mutationFn: (file: File) => customerGiftsApi.import(file),
		onSuccess: (result) => {
			invalidateList()
			toast.success(`导入完成：成功 ${result.success} 条，失败 ${result.failed} 条`)
		},
		onError: (error) => showError(error, '导入送礼记录失败'),
	})

	const shippingMutation = useMutation({
		mutationFn: (file: File) => customerGiftsApi.updateShippingImport(file),
		onSuccess: (result) => {
			invalidateList()
			toast.success(`物流单号更新完成：成功 ${result.success} 条，失败 ${result.failed} 条`)
		},
		onError: (error) => showError(error, '更新物流单号失败'),
	})

	const exportMutation = useMutation({
		mutationFn: () => customerGiftsApi.export(selected),
		onSuccess: () => toast.success('导出成功'),
		onError: (error) => showError(error, '导出失败'),
	})

	const deleteMutation = useMutation({
		mutationFn: (gift: CustomerGift) => customerGiftsApi.delete(gift.id),
		onSuccess: () => {
			invalidateList()
			toast.success('送礼记录已删除')
		},
		onError: (error) => showError(error, '删除失败'),
	})

	const batchDeleteMutation = useMutation({
		mutationFn: () => customerGiftsApi.batchDelete(selected),
		onSuccess: () => {
			invalidateList()
			toast.success('批量删除成功')
		},
		onError: (error) => showError(error, '批量删除失败'),
	})

	const gifts = data?.list ?? []
	const hasSelection = selected.length > 0
	const allSelected = gifts.length > 0 && gifts.every((gift) => selected.includes(gift.id))

	const handleSearch = () => {
		setSearchFilters(cleanFilters(filters))
		setPage(1)
	}

	const handleReset = () => {
		setFilters({ scope: 'my' })
		setSearchFilters({ scope: 'my' })
		setPage(1)
	}

	const handleDateChange = (dateValue: { start_date?: string; end_date?: string }) => {
		setFilters({
			...filters,
			start_date: dateValue.start_date,
			end_date: dateValue.end_date,
		})
	}

	const handleSelectAll = (checked: boolean) => {
		setSelected(checked ? gifts.map((gift) => gift.id) : [])
	}

	const handleSelectOne = (id: number, checked: boolean) => {
		setSelected((current) => checked ? [...current, id] : current.filter((item) => item !== id))
	}

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'import' | 'shipping') => {
		const file = event.target.files?.[0]
		event.target.value = ''
		if (!file) return
		if (type === 'import') {
			importMutation.mutate(file)
			return
		}
		shippingMutation.mutate(file)
	}

	const handleDelete = (gift: CustomerGift) => {
		if (!window.confirm(`确定删除送礼记录 #${gift.id} 吗？删除后会返还礼物库存。`)) return
		deleteMutation.mutate(gift)
	}

	return (
		<div className="space-y-5">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">送礼管理</h1>
					<p className="mt-1 text-sm text-muted-foreground">按员工记录礼物寄送、物流单号和库存扣减</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<PermissionGuard permission="customer_gift:import">
						<Button type="button" variant="outline" onClick={() => customerGiftsApi.template()}>
							<Download className="mr-1.5 h-4 w-4" />
							下载模板
						</Button>
						<Button type="button" disabled={importMutation.isPending} onClick={() => importInputRef.current?.click()}>
							<Upload className="mr-1.5 h-4 w-4" />
							导入记录
						</Button>
						<input ref={importInputRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={(event) => handleFileChange(event, 'import')} />
					</PermissionGuard>
					<PermissionGuard permission="customer_gift:update_shipping">
						<Button type="button" variant="outline" disabled={shippingMutation.isPending} onClick={() => shippingInputRef.current?.click()}>
							<Truck className="mr-1.5 h-4 w-4" />
							更新单号
						</Button>
						<input ref={shippingInputRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={(event) => handleFileChange(event, 'shipping')} />
					</PermissionGuard>
				</div>
			</div>

			<Card>
				<CardContent className="pt-6">
					<div className="space-y-4">
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
							<FilterInput placeholder="寄件人" value={filters.sender} onChange={(value) => setFilters({ ...filters, sender: value })} />
							<FilterInput placeholder="寄件人手机" value={filters.sender_mobile} onChange={(value) => setFilters({ ...filters, sender_mobile: value })} />
							<FilterInput placeholder="收件人" value={filters.receiver} onChange={(value) => setFilters({ ...filters, receiver: value })} />
							<FilterInput placeholder="收件人手机" value={filters.receiver_mobile} onChange={(value) => setFilters({ ...filters, receiver_mobile: value })} />
						</div>
						<div className="flex flex-wrap items-center gap-4">
							<div>
								<DepartmentScopeFilter
									value={{ scope: filters.scope || 'my', department_id: filters.department_id }}
									onChange={(scopeValue) => setFilters({ ...filters, department_id: undefined, ...scopeValue })}
								/>
							</div>
							<Select value={filters.product_id ? String(filters.product_id) : 'all'} onValueChange={(value) => setFilters({ ...filters, product_id: value === 'all' ? undefined : Number(value) })}>
								<SelectTrigger className="w-[160px]">
									<SelectValue placeholder="全部礼物" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">全部礼物</SelectItem>
									{(productOptions ?? []).map((product) => (
										<SelectItem key={product.id} value={String(product.id)}>
											{product.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FilterInput className="w-[200px]" placeholder="物流单号" value={filters.code} onChange={(value) => setFilters({ ...filters, code: value })} />
							<DateRangePicker
								value={{
									start_date: filters.start_date,
									end_date: filters.end_date,
								}}
								onChange={handleDateChange}
							/>
							<div className="flex gap-2">
								<Button type="button" onClick={handleSearch} disabled={isFetching}>
									<Search className="mr-1.5 h-4 w-4" />
									查询
								</Button>
								<Button type="button" variant="outline" onClick={handleReset}>
									<RotateCcw className="mr-1.5 h-4 w-4" />
									重置
								</Button>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardContent className="p-0">
					{hasSelection && (
						<div className="flex items-center justify-between gap-3 border-b px-4 py-3">
							<span className="text-sm text-muted-foreground">已选择 {selected.length} 条送礼记录</span>
							<div className="flex flex-wrap items-center gap-2">
								<PermissionGuard permission="customer_gift:export">
									<Button type="button" size="sm" variant="outline" disabled={exportMutation.isPending} onClick={() => exportMutation.mutate()}>
										<FileUp className="mr-1.5 h-3.5 w-3.5" />
										导出选中
									</Button>
								</PermissionGuard>
								<PermissionGuard permission="customer_gift:delete">
									<Button type="button" size="sm" variant="outline" disabled={batchDeleteMutation.isPending} onClick={() => setBatchConfirmOpen(true)}>
										<Trash2 className="mr-1.5 h-3.5 w-3.5" />
										批量删除
									</Button>
								</PermissionGuard>
							</div>
						</div>
					)}
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-12">
									<Checkbox aria-label="选择全部送礼记录" checked={allSelected} onCheckedChange={(checked) => handleSelectAll(checked === true)} />
								</TableHead>
								<TableHead className="w-20">ID</TableHead>
								<TableHead className="w-28">员工</TableHead>
								<TableHead>寄件信息</TableHead>
								<TableHead>收件信息</TableHead>
								<TableHead className="w-36">礼物</TableHead>
								<TableHead className="w-40">物流单号</TableHead>
								<TableHead className="w-28">录入时间</TableHead>
								<TableHead className="w-28 text-right">操作</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={9} className="h-28 text-center text-muted-foreground">加载中...</TableCell>
								</TableRow>
							) : gifts.length === 0 ? (
								<TableRow>
									<TableCell colSpan={9} className="h-32 text-center text-muted-foreground">
										<div className="flex flex-col items-center gap-2">
											<PackageOpen className="h-8 w-8 text-muted-foreground/60" />
											<span>暂无送礼记录</span>
										</div>
									</TableCell>
								</TableRow>
							) : (
								gifts.map((gift) => (
									<TableRow key={gift.id}>
										<TableCell>
											<Checkbox aria-label={`选择送礼记录${gift.id}`} checked={selected.includes(gift.id)} onCheckedChange={(checked) => handleSelectOne(gift.id, checked === true)} />
										</TableCell>
										<TableCell>{gift.id}</TableCell>
										<TableCell>{gift.user?.nickname || gift.user?.username || '-'}</TableCell>
										<TableCell>
											<ContactBlock name={gift.sender} mobile={gift.sender_mobile} address={gift.sender_address} />
										</TableCell>
										<TableCell>
											<ContactBlock name={gift.receiver} mobile={gift.receiver_mobile} address={gift.receiver_address} />
										</TableCell>
										<TableCell>
											<div className="space-y-1">
												<Badge variant="secondary">{gift.type}</Badge>
												<div className="text-xs text-muted-foreground">数量 {gift.number}</div>
											</div>
										</TableCell>
										<TableCell>
											<span className="font-mono text-sm">{gift.code || '-'}</span>
										</TableCell>
										<TableCell>{gift.record_date}</TableCell>
										<TableCell className="text-right">
											<PermissionGuard permission="customer_gift:delete">
												<Button type="button" size="sm" variant="ghost" onClick={() => handleDelete(gift)}>
													<Trash2 className="mr-1.5 h-4 w-4" />
													删除
												</Button>
											</PermissionGuard>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<ListPagination
				page={page}
				pageSize={pageSize}
				total={data?.total ?? 0}
				loading={isFetching}
				onPageChange={setPage}
				onPageSizeChange={(nextPageSize) => {
					setPageSize(nextPageSize)
					setPage(1)
				}}
			/>
			<BatchDeleteDialog
				count={selected.length}
				open={batchConfirmOpen}
				isSubmitting={batchDeleteMutation.isPending}
				onOpenChange={setBatchConfirmOpen}
				onConfirm={() => batchDeleteMutation.mutate(undefined, { onSuccess: () => setBatchConfirmOpen(false) })}
			/>
		</div>
	)
}

function FilterInput({ className, placeholder, value, onChange }: { className?: string; placeholder: string; value?: string; onChange: (value: string | undefined) => void }) {
	return (
		<div className={className}>
			<Input placeholder={placeholder} value={value ?? ''} onChange={(event) => onChange(event.target.value || undefined)} />
		</div>
	)
}

function ContactBlock({ name, mobile, address }: { name: string; mobile: string; address: string }) {
	return (
		<div className="min-w-0 space-y-1">
			<div className="flex items-center gap-2">
				<Send className="h-3.5 w-3.5 text-muted-foreground" />
				<span className="font-medium">{name}</span>
				<span className="font-mono text-xs text-muted-foreground">{mobile}</span>
			</div>
			<div className="max-w-[260px] truncate text-xs text-muted-foreground">{address}</div>
		</div>
	)
}

function cleanFilters(filters: FilterValues): FilterValues {
	return Object.fromEntries(
		Object.entries(filters).filter(([, value]) => value !== undefined && value !== '')
	) as FilterValues
}
